import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import DataTable from "@/components/DataTable";
import StatusBadge from "@/components/StatusBadge";
import { GhostButton } from "@/components/FormKit";
import { fmtDateTime } from "@/lib/format";
import { toast } from "sonner";
import { Mail, Phone } from "lucide-react";

const STATUS_OPTIONS = ["new", "contacted", "converted", "rejected"];

export default function DemoRequests() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/platform/demo-requests");
      setItems(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const changeStatus = async (id, status) => {
    try {
      await api.patch(`/platform/demo-requests/${id}`, { status });
      toast.success(`Marked ${status}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const counts = STATUS_OPTIONS.reduce((acc, s) => {
    acc[s] = items.filter((r) => r.status === s).length;
    return acc;
  }, {});

  return (
    <div className="space-y-8" data-testid="platform-demo-requests-page">
      <PageHeader
        eyebrow="Pipeline"
        title="Demo requests"
        description="Inbound leads from the Savvy landing page — assign, contact, convert."
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {STATUS_OPTIONS.map((s, i) => (
          <div
            key={s}
            className={`grid-surface p-5 ${i === 0 ? "border-l-4 border-l-brand-500" : ""}`}
            data-testid={`demo-count-${s}`}
          >
            <div className="overline text-stone-500">{s}</div>
            <div className="font-display text-4xl font-black mt-2">{counts[s]}</div>
          </div>
        ))}
      </div>

      <DataTable
        testId="demo-requests-table"
        columns={[
          {
            key: "created_at",
            label: "Received",
            render: (r) => <span className="font-mono text-xs">{fmtDateTime(r.created_at)}</span>,
          },
          {
            key: "company_name",
            label: "Company",
            render: (r) => (
              <div>
                <div className="font-bold">{r.company_name}</div>
                <div className="text-xs text-stone-500">{r.location}</div>
              </div>
            ),
          },
          {
            key: "contact",
            label: "Contact",
            render: (r) => (
              <div>
                <div className="font-bold">{r.contact_person}</div>
                <a
                  href={`mailto:${r.email}`}
                  className="text-xs text-brand-600 hover:text-brand-700 font-mono flex items-center gap-1"
                  data-testid={`demo-email-${r.id}`}
                >
                  <Mail size={11} /> {r.email}
                </a>
                <div className="text-xs text-stone-500 font-mono flex items-center gap-1">
                  <Phone size={11} /> {r.phone}
                </div>
              </div>
            ),
          },
          {
            key: "message",
            label: "Message",
            render: (r) =>
              r.message ? (
                <div className="text-sm text-stone-700 max-w-sm whitespace-pre-wrap line-clamp-3">
                  {r.message}
                </div>
              ) : (
                <span className="text-stone-400 text-xs">—</span>
              ),
          },
          {
            key: "status",
            label: "Status",
            render: (r) => <StatusBadge value={r.status} />,
          },
          {
            key: "actions",
            label: "Actions",
            render: (r) => (
              <div className="flex flex-col gap-1 items-start">
                {STATUS_OPTIONS.filter((s) => s !== r.status).map((s) => (
                  <GhostButton
                    key={s}
                    onClick={() => changeStatus(r.id, s)}
                    testId={`mark-${s}-${r.id}`}
                  >
                    Mark {s}
                  </GhostButton>
                ))}
              </div>
            ),
          },
        ]}
        rows={items}
        empty={loading ? "Loading…" : "No demo requests yet."}
      />
    </div>
  );
}
